import { useState } from "react";

import { LabObservationLevel, LabObservationLevelLabels } from "@/Common/Api/ManualResults/Dtos/LabObservationsDto";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ObservationEntryFormData } from "@/Common/Models/ManualLabResultsEntry/ObservationEntryFromData";
import { Button, LinkButton } from "@/DesignSystem";
import { ExpandableSection } from "../ExpandableSection";
import { ObservationEntryForm } from "./ObservationEntryForm";

import styles from "./ManualLabResultEntryFormList.module.scss";

interface ManualLabResultEntryFormListProps {
    readonly data: ObservationEntryFormData[];
    readonly onChange: (data: ObservationEntryFormData[]) => void;
}

function ObservationTitle({ observationEntryFormData, onDeleteClick, hasMinResultEntries }: Readonly<{
    observationEntryFormData: ObservationEntryFormData,
    onDeleteClick: () => void,
    hasMinResultEntries: boolean,
}>): JSX.Element {
    const { getText } = useTranslation();

    const flagIsAbnormal = observationEntryFormData.level != null && observationEntryFormData.level !== LabObservationLevel.Normal;
    const flagValue = observationEntryFormData.level == null ? "" : LabObservationLevelLabels[observationEntryFormData.level];

    const parts = [
        observationEntryFormData.AnalysisModel?.DescriptionShort ?? "",
        observationEntryFormData.result && (
            <span
                className={`${styles.resultValue} ${flagIsAbnormal ? styles.abnormal : ""}`}
            >
                {observationEntryFormData.result}
            </span>
        ),
        !IsNullOrWhiteSpace(flagValue) && <span className={`${flagIsAbnormal ? styles.abnormal : ""}`}>{flagValue}</span>
    ].filter(Boolean);

    let titleElement: JSX.Element = <>{getText("ResultLabel")}</>;
    if (parts.length > 0)
        titleElement = <>{parts.reduce((acc, curr, idx) => idx === 0 ? [curr] : [...acc, " - ", curr], [] as React.ReactNode[])}</>;

    return (
        <div className={styles.titleContainer}>
            <p className={styles.titleResult}>
                {titleElement}
            </p>
            {hasMinResultEntries && <LinkButton variant="error" label={getText("DeleteLabel")} onClick={onDeleteClick} />}
        </div>
    );
}

function ManualLabResultEntryFormList({ data, onChange }: ManualLabResultEntryFormListProps): JSX.Element {
    const minResultEntries = 1;

    const { getText } = useTranslation();
    const [expandedResultId, setExpandedResultId] = useState<string>(() => data[data.length - 1 || 0].id);
    const [isValid, setIsValid] = useState<boolean>(false);

    const handleResultEntryChevronClick = (nextResult: ObservationEntryFormData): void => {
        setExpandedResultId(nextResult.id);
    };

    const handleDeleteResultEntry = (resultToDeleteId: string, resultToDeleteIndex: number): void => {
        if (data.length <= minResultEntries)
            return;

        const newResultEntries = [...data];
        newResultEntries.splice(resultToDeleteIndex, 1);

        if (resultToDeleteId == expandedResultId) {
            const nextExpandedResult = newResultEntries[resultToDeleteIndex] || newResultEntries[resultToDeleteIndex - 1];

            setExpandedResultId(nextExpandedResult.id);
            setIsValid(!!nextExpandedResult.analysis && !!nextExpandedResult.result);
        }
        onChange(newResultEntries);
    };

    const handleResultEntryChange = (currentResult: ObservationEntryFormData, mutation: (p: ObservationEntryFormData) => void): void => {
        mutation(currentResult);

        const newResultEntries = [...data];
        newResultEntries[data.findIndex(result => result.id == currentResult.id)] = currentResult;

        setIsValid(currentResult.isValid());
        onChange(newResultEntries);
    };

    const handleAddResultEntry = (): void => {
        if (!isValid)
            return;

        const newEntry = ObservationEntryFormData.createEmpty();

        setIsValid(false);
        setExpandedResultId(newEntry.id);

        onChange(
            [
                ...data,
                newEntry
            ]
        );
    };

    return (
        <>
            {data.map((result, index) => (
                <ExpandableSection
                    key={result?.id}
                    dataCy={`expandable-manual-lab-results-form-${result.id}`}
                    isOpen={result.id == expandedResultId}
                    title={(
                        <ObservationTitle
                            observationEntryFormData={result}
                            onDeleteClick={() => handleDeleteResultEntry(result.id, index)}
                            hasMinResultEntries={data.length > minResultEntries}
                        />
                    )}
                    onChevronClick={() => handleResultEntryChevronClick(result)}
                    variant="brand"
                    category="secondary"
                    showChevronIcon={result.id !== expandedResultId}
                >
                    <ObservationEntryForm
                        data={data[index]}
                        dataCy="result-entry-form"
                        onChange={p => handleResultEntryChange(result, p)}
                    />
                </ExpandableSection>
            ))}
            <div className={styles.buttonContainer}>
                <Button
                    disabled={!isValid}
                    variant="brand"
                    category="secondary"
                    onClick={() => handleAddResultEntry()}
                    label={getText("ManualResults_AddNewResultLabel")}
                    dataCy="add-result-button"
                />
            </div>
        </>
    );
}

export { ManualLabResultEntryFormList };
